// Musette's state. The live store is the D1 database behind /api: one document per
// collection, each written back whole with the `updated` it was read at (a stale write is
// refused and re-read). Signed out, or with no signal, the screens run on the last copy
// this device saw, or on the seed, and say so in the drawer.
import { setKitThresholds } from "./weather";
import { haptic } from "./haptic";
import { useEffect, useState } from "preact/hooks";
import { emptyState } from "../data/empty.ts";
import type { DayStatus, State, Feel, Route, Legs } from "./types";
import { homeOf } from "../data/empty.ts";

type DocId = "ramp" | "plan" | "routes" | "rides" | "event" | "coach" | "weight" | "settings" | "log";
const DOCS: DocId[] = ["ramp", "plan", "routes", "rides", "event", "coach", "weight", "settings", "log"];

// How each document maps onto the screens' State.
function toDoc(s: State, id: DocId): unknown {
  switch (id) {
    case "ramp": return { weeks: s.ramp, status: s.rampStatus };
    case "event": return { stages: s.stages };
    default: return s[id];
  }
}
function fromDoc(s: State, id: DocId, v: any) {
  switch (id) {
    case "ramp": s.ramp = v.weeks; s.rampStatus = v.status; break;
    case "event": s.stages = v.stages; break;
    default: (s as any)[id] = v;
  }
}

export type Sync = "seed" | "signed-out" | "live" | "offline";
export type Meta = { sync: Sync; updated: Partial<Record<DocId, string>>; strava?: { configured: boolean; connected: boolean; last?: string }; google?: { configured: boolean; connected: boolean }; thinking?: string };

const CACHE = "musette.cache.v2";
let current: State = emptyState();
let meta: Meta = { sync: "seed", updated: {} };
try {
  const raw = localStorage.getItem(CACHE);
  if (raw) { const c = JSON.parse(raw); current = c.state; meta = { ...c.meta, sync: "offline" }; }
} catch { /* private mode or cleared storage: the seed it is */ }

const subs = new Set<() => void>();
export const getState = () => current;
export const getMeta = () => meta;
function emit() {
  setKitThresholds(current.settings?.kit);
  subs.forEach((f) => f());
  try { localStorage.setItem(CACHE, JSON.stringify({ state: current, meta })); } catch { /* fine */ }
}

/** Subscribe a component to Musette's state. */
export function useMusette(): State {
  const [, bump] = useState(0);
  useEffect(() => { const f = () => bump((n) => n + 1); subs.add(f); return () => { subs.delete(f); }; }, []);
  return current;
}
export function useSync(): Meta {
  useMusette();
  return meta;
}

async function api(path: string, init?: RequestInit) {
  const res = await fetch(path, { credentials: "same-origin", headers: { "content-type": "application/json" }, ...init });
  const body = await res.json().catch(() => ({}));
  return { status: res.status, body: body as any };
}

/** Read every document. A document the rider hasn't made yet stays empty until they do. */
export async function refresh(): Promise<Sync> {
  let r;
  try { r = await api("/api/state"); } catch { meta = { ...meta, sync: "offline" }; emit(); return meta.sync; }
  if (r.status === 401) { meta = { ...meta, sync: "signed-out" }; emit(); return meta.sync; }
  if (r.status !== 200) { meta = { ...meta, sync: "offline" }; emit(); return meta.sync; }

  const next = emptyState();
  const updated: Meta["updated"] = {};
  const docs = r.body?.docs as Record<string, { v: unknown; updated: string }> | undefined;
  // A train's wifi sign-in page answers 200 with HTML: treat it as no signal, keep the last copy.
  if (!docs || typeof docs !== "object") { meta = { ...meta, sync: "offline" }; emit(); return meta.sync; }
  for (const id of DOCS) {
    if (docs[id]) { fromDoc(next, id, docs[id].v); updated[id] = docs[id].updated; }
  }
  current = next;
  meta = { ...meta, sync: "live", updated };
  emit();
  return "live";
}

/** Change one document and write it back; on a stale write, re-read and apply the change again. */
async function change(id: DocId, edit: (s: State) => void, tries = 2): Promise<boolean> {
  const s = structuredClone(current);
  edit(s);
  current = s;
  emit();
  if (meta.sync !== "live") return false;
  const w = await api("/api/patch", { method: "POST", body: JSON.stringify({ id, base: meta.updated[id] ?? null, v: toDoc(s, id) }) }).catch(() => null);
  if (w?.status === 200) { meta.updated[id] = w.body.updated; emit(); return true; }
  if (w?.status === 409 && tries > 0) { await refresh(); return change(id, edit, tries - 1); }
  return false;
}

export function setStatus(date: string, status: DayStatus) {
  return change("plan", (s) => { for (const w of s.plan) for (const d of w.days) if (d.date === date) d.status = status; });
}

export async function signIn(key: string): Promise<string | null> {
  const r = await api("/api/signin", { method: "POST", body: JSON.stringify({ key }) }).catch(() => null);
  if (!r) return "No signal. Try again when you have one.";
  if (r.status !== 200) return r.body.error ?? "That did not work.";
  await refresh();
  await stravaSync();
  return null;
}

/** Ask the server to pull new Strava rides (it throttles itself), then re-read if any landed. */
export async function stravaSync() {
  if (meta.sync !== "live") return;
  const st = await api("/api/strava/status").catch(() => null);
  if (!st || st.status !== 200) return;
  meta = { ...meta, strava: st.body };
  emit();
  if (!st.body.connected) return;
  const r = await api("/api/strava/sync", { method: "POST" }).catch(() => null);
  if (r?.status === 200) {
    meta = { ...meta, strava: { ...st.body, last: r.body.last } };
    if (r.body.added > 0) await refresh(); else emit();
  }
}

export async function googleStatus() {
  if (meta.sync !== "live") return;
  const g = await api("/api/google/status").catch(() => null);
  if (g?.status === 200) { meta = { ...meta, google: g.body }; emit(); }
}

export async function boot() {
  if ((await refresh()) === "live") { await googleStatus(); await stravaSync(); }
}

/**
 * Put rides in the diary. The tick is the yes. Each ride goes in unless a ride is already
 * there at that time; either way it is marked booked. Without the diary connected the rides
 * stay ticked, and Claude books them on the next chat.
 */
export async function bookRides(dates: string[]): Promise<string> {
  haptic("thud");
  const s = current;
  const days = s.plan.flatMap((w) => w.days).filter((d) => dates.includes(d.date) && d.type === "ride" && d.start && d.end);
  if (meta.sync !== "live" || !meta.google?.connected) {
    await change("plan", (x) => { for (const w of x.plan) for (const d of w.days) if (dates.includes(d.date)) d.status = "ticked"; });
    return "Ticked. Connect your diary from the menu and they go straight in.";
  }
  const { forecastAt } = await import("./weather");
  const rides = await Promise.all(days.map(async (d) => {
    const route = d.route ? s.routes.find((r) => r.id === d.route) : undefined;
    const name = route?.n ?? "City loop, lit";
    const fc = d.kit ? null : await forecastAt(homeOf(s).lat, homeOf(s).lon, d.date, d.start!);
    const kit = d.kit ?? (fc ? `${fc.kit} (feels ${fc.feels}C, forecast when booked)` : "");
    const lines = [d.why, route ? `${route.km} km, ${route.gain} m.` : "", kit ? `Kit: ${kit}` : "", d.lunch ? `Lunch: ${d.lunch}` : "", "From Musette."].filter(Boolean);
    return { date: d.date, start: d.start!, end: d.end!, title: `Ride · ${name}`, description: lines.join("\n") };
  }));
  const r = await api("/api/calendar/book", { method: "POST", body: JSON.stringify({ rides }) }).catch(() => null);
  if (!r || r.status !== 200) return r?.body?.error ?? "The diary did not answer. Try again in a moment.";
  const ok = (r.body.results as { date: string; status: string }[]).filter((x) => x.status !== "failed").map((x) => x.date);
  await change("plan", (x) => { for (const w of x.plan) for (const d of w.days) if (ok.includes(d.date)) d.status = "booked"; });
  const added = (r.body.results as { status: string }[]).filter((x) => x.status === "added").length;
  const failed = rides.length - ok.length;
  return failed ? `${ok.length} in the diary, ${failed} did not go in.` : added ? `${added} added to the diary.` : "Already in the diary.";
}

/** Kyle points the next block in a direction; Claude reworks the ramp around it. */
export function setDirection(id: string) {
  return change("coach", (s) => {
    s.coach = { ...(s.coach ?? { say: "", wk: "" }), direction: { id, at: new Date().toISOString() } };
  });
}

/** Build a week from the diary and keep it as proposed. An existing week keeps its ticks and bookings. */
export async function buildWeekFromDiary(monday: string): Promise<string> {
  if (meta.sync !== "live") return "Sign in first.";
  if (!meta.google?.connected) return "Connect your diary from the menu first.";
  const r = await api(`/api/calendar/week?from=${monday}`).catch(() => null);
  if (!r || r.status !== 200) return r?.body?.error ?? "The diary did not answer. Try again in a moment.";
  const { buildWeek } = await import("./planner");
  const week = buildWeek(monday, r.body.events, current);
  await change("plan", (s) => {
    const old = s.plan.find((w) => w.monday === monday);
    if (old) for (const d of week.days) {
      const was = old.days.find((x) => x.date === d.date);
      if (was && (was.status === "ticked" || was.status === "booked" || was.status === "done") && d.status !== "booked") Object.assign(d, was);
    }
    s.plan = [...s.plan.filter((w) => w.monday !== monday), week].sort((a, b) => (a.monday < b.monday ? -1 : 1));
  });
  const rides = week.days.filter((d) => d.type === "ride").length;
  return `Built from your diary: ${rides} ${rides === 1 ? "ride" : "rides"}. Tick the ones you want.`;
}

/** Phone notifications. On iPhone they work only from the home-screen app (iOS 16.4 and later). */
export type PushState = "unsupported" | "home-screen" | "off" | "on" | "blocked";
export async function pushState(): Promise<PushState> {
  if (!("serviceWorker" in navigator)) return "unsupported";
  const standalone = matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true;
  if (!("PushManager" in window)) return /iPhone|iPad/.test(navigator.userAgent) && !standalone ? "home-screen" : "unsupported";
  if (Notification.permission === "denied") return "blocked";
  const reg = await navigator.serviceWorker.ready;
  return (await reg.pushManager.getSubscription()) ? "on" : "off";
}
const fromB64u = (s: string) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4)), (c) => c.charCodeAt(0));
export async function pushOn(): Promise<string> {
  if (meta.sync !== "live") return "Sign in first.";
  if ((await Notification.requestPermission()) !== "granted") return "Notifications are blocked. Allow them in Settings for Musette.";
  const k = await api("/api/push/key").catch(() => null);
  if (!k || k.status !== 200) return "Musette did not answer. Try again in a moment.";
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromB64u(k.body.key) }));
  const r = await api("/api/push/subscribe", { method: "POST", body: JSON.stringify({ subscription: sub.toJSON() }) }).catch(() => null);
  if (!r || r.status !== 200) return "That did not save. Try again in a moment.";
  const t = await api("/api/push/test", { method: "POST" }).catch(() => null);
  return t?.body?.sent ? "On. A test notification is on its way." : "On.";
}

// ——— The verbs. Every change is the user's own tap; the diary follows only where it already holds the ride.

const dayOf = (s: State, date: string) => s.plan.flatMap((w) => w.days).find((d) => d.date === date);

/** Move a ride to a new time (and, optionally, a new day). A booked ride moves in the diary too. */
export async function moveRide(date: string, to: { date?: string; start: string; end: string; route?: string }): Promise<string> {
  const was = dayOf(current, date);
  if (!was || was.type !== "ride" || !was.start || !was.end) return "No ride there to move.";
  const from = { start: was.start, end: was.end };
  const target = to.date ?? date;
  const booked = was.status === "booked";
  await change("plan", (s) => {
    const a = dayOf(s, date)!;
    if (target === date) { a.start = to.start; a.end = to.end; if (to.route) a.route = to.route; return; }
    const b = dayOf(s, target);
    if (!b) return;
    const ride = { ...a, date: target, start: to.start, end: to.end, route: to.route ?? a.route };
    Object.assign(b, ride, { date: target });
    a.type = "rest"; delete a.start; delete a.end; delete a.route; delete a.status; delete a.kit; delete a.lunch; a.why = `Moved to ${target}`;
  });
  if (!booked) return target === date ? `Moved to ${to.start}.` : "Moved.";
  if (meta.sync !== "live" || !meta.google?.connected) return "Moved here. Connect the diary from the menu and it follows.";
  const route = (to.route ?? was.route) ? current.routes.find((r) => r.id === (to.route ?? was.route)) : undefined;
  const r = await api("/api/calendar/move", { method: "POST", body: JSON.stringify({ date, from, to: { date: target, start: to.start, end: to.end }, title: to.route ? `Ride · ${route?.n ?? "City loop, lit"}` : undefined }) }).catch(() => null);
  if (!r || r.status !== 200) return "Moved here; the diary did not answer, so it still shows the old time.";
  return r.body.status === "none" ? "Moved. It was not in the diary to move." : `Moved, and the diary has it at ${to.start}.`;
}

/** Call a ride off. With fromDiary, the booked event comes out of the calendar as well. */
export async function cancelRide(date: string, fromDiary: boolean): Promise<string> {
  const was = dayOf(current, date);
  if (!was || !was.start || !was.end) return "No ride there.";
  const from = { start: was.start, end: was.end };
  await change("plan", (s) => { const d = dayOf(s, date); if (d) d.status = "cancelled"; });
  if (!fromDiary || was.status !== "booked") return "Off. The rest of the week holds.";
  if (meta.sync !== "live" || !meta.google?.connected) return "Off here. The diary is not connected, so it is still there.";
  const r = await api("/api/calendar/move", { method: "POST", body: JSON.stringify({ date, from }) }).catch(() => null);
  return r?.status === 200 ? "Off, and out of the diary." : "Off here; the diary did not answer, so it is still there.";
}

/** Put a cancelled ride back as proposed. */
export function restoreRide(date: string) {
  return change("plan", (s) => { const d = dayOf(s, date); if (d) d.status = "proposed"; });
}

/** Swap the route on a day's ride, resizing it. Not booked yet: only the plan changes. */
export function setRoute(date: string, route: string, start: string, end: string) {
  const was = dayOf(current, date);
  if (was?.status === "booked") return moveRide(date, { start, end, route });
  return change("plan", (s) => { const d = dayOf(s, date); if (d) { d.route = route; d.start = start; d.end = end; } }).then(() => "Route changed.");
}

export function setFeel(date: string, feel: Feel | null) {
  haptic();
  return change("log", (s) => { s.log = { ...s.log, feel: { ...(s.log?.feel ?? {}) }, ready: { ...(s.log?.ready ?? {}) } }; if (feel) s.log.feel[date] = feel; else delete s.log.feel[date]; });
}

export function setLegs(date: string, legs: Legs | null) {
  haptic();
  return change("log", (s) => { s.log = { ...s.log, legs: { ...(s.log?.legs ?? {}) } }; if (legs) s.log.legs![date] = legs; else delete s.log.legs![date]; });
}

/** His review of a Komoot find: keep it for later, or not for me. */
export function markExplore(id: string, v: "keep" | "skip" | null) {
  haptic();
  return change("log", (s) => { s.log = { ...s.log, explore: { ...(s.log?.explore ?? {}) } }; if (v) s.log.explore![id] = v; else delete s.log.explore![id]; });
}

export function toggleReady(date: string, item: string) {
  haptic();
  return change("log", (s) => {
    s.log = { ...s.log, feel: { ...(s.log?.feel ?? {}) }, ready: { ...(s.log?.ready ?? {}) } };
    const have = new Set(s.log.ready[date] ?? []);
    if (have.has(item)) have.delete(item); else have.add(item);
    s.log.ready[date] = [...have];
  });
}

/** Ride a rest day after all: the lit city loop, proposed, after work on a weekday or at 09:00 at the weekend. */
export function addRide(date: string) {
  const dw = new Date(date + "T12:00:00Z").getUTCDay();
  const [start, end] = dw === 0 || dw === 6 ? ["09:00", "10:30"] : ["18:30", "20:00"];
  return change("plan", (s) => { const d = dayOf(s, date); if (d) Object.assign(d, { type: "ride", route: "city-loop", start, end, status: "proposed", why: "Added from the week" }); });
}

/** Choose or unchoose a proposed ride (the Friday tick). */
export function toggleChosen(date: string) {
  return change("plan", (s) => { const d = dayOf(s, date); if (d && (d.status === "proposed" || d.status === "ticked")) d.status = d.status === "ticked" ? "proposed" : "ticked"; });
}

const logOf = (s: State) => ({ feel: { ...(s.log?.feel ?? {}) }, ready: { ...(s.log?.ready ?? {}) }, asks: [...(s.log?.asks ?? [])], follow: [...(s.log?.follow ?? [])] });

/**
 * Ask the coach. The ask is saved to the log first, then the live coach answers it in seconds
 * (Claude through the API, on Musette's server). Without the live key, or with no signal, it
 * waits for the cloud coach's next run (06:15, 12:15, 15:45, 20:45). Returns how it went.
 */
export async function askCoach(text: string): Promise<"live" | "queued" | "offline"> {
  const t = text.trim();
  if (!t) return "offline";
  const at = new Date().toISOString();
  const saved = await change("log", (s) => { s.log = logOf(s); s.log.asks!.push({ at, text: t }); });
  if (!saved) return "offline";
  meta = { ...meta, thinking: at }; emit();
  const r = await api("/api/coach/answer", { method: "POST", body: JSON.stringify({ at }) }).catch(() => null);
  meta = { ...meta, thinking: undefined };
  await refresh();
  return r?.body?.live ? "live" : "queued";
}

/** Tap a coach button: do what it says (the tap is the yes), then mark it done on the ask. */
export async function applyAction(at: string, i: number): Promise<string> {
  haptic();
  const ask = current.log?.asks?.find((a) => a.at === at);
  const a = ask?.actions?.[i];
  if (!a || ask?.done?.includes(i)) return "Already done.";
  let said = "Done.";
  if (a.kind === "move" && a.date && a.start && a.end) said = await moveRide(a.date, { date: a.to ?? a.date, start: a.start, end: a.end, route: a.route });
  else if (a.kind === "cancel" && a.date) said = await cancelRide(a.date, true);
  else if (a.kind === "add" && a.date) { await addRide(a.date); if (a.start && a.end) await change("plan", (s) => { const d = dayOf(s, a.date!); if (d) { d.start = a.start; d.end = a.end; if (a.route) d.route = a.route; } }); said = `${a.label.replace(/^Add /, "")}: proposed.`; }
  else if (a.kind === "route" && a.date && a.route && a.start && a.end) said = await setRoute(a.date, a.route, a.start, a.end);
  else if (a.kind === "direction" && a.id) { await setDirection(a.id); said = "Direction set. The cloud coach reworks the plan on its next run."; }
  await change("log", (s) => { s.log = logOf(s); const x = s.log.asks!.find((y) => y.at === at); if (x) x.done = [...new Set([...(x.done ?? []), i])]; });
  return said;
}

/** Change Kyle's rules. The planner, the walls, the kit and the notices all read these. */
export function setSettings(patch: Partial<State["settings"]>) {
  return change("settings", (s) => { s.settings = { ...s.settings, ...patch }; });
}

/** Server reads that aren't documents: a ride's detail, every ride's map line, his bikes. */
/** intervals.icu fitness, cached by the server hourly; null until the keys are in. Fetched once a session. */
let icuCache: Promise<import("./fitness").Icu | null> | null = null;
export function intervalsFit(): Promise<import("./fitness").Icu | null> {
  icuCache ??= api("/api/intervals/fit").then((r) => (r?.status === 200 && r.body?.fit?.days?.length ? { days: r.body.fit.days, perKm: r.body.fit.perKm } : null)).catch(() => null);
  return icuCache;
}
/** What is set up and what is not (keys exist, connections hold, the timer ran). */
export async function setupStatus(): Promise<Record<string, any> | null> {
  const r = await api("/api/setup").catch(() => null);
  return r?.status === 200 ? r.body : null;
}
export async function rideDetail(id: number) { const r = await api(`/api/rides/${id}`).catch(() => null); return r?.status === 200 ? r.body : null; }
export async function routeOuts(): Promise<Record<string, number | null>> { const r = await api("/api/routes/bearings").catch(() => null); return r?.status === 200 ? r.body.outs ?? {} : {}; }
export async function rideMaps(): Promise<Record<string, string>> { const r = await api("/api/rides/maps").catch(() => null); return r?.status === 200 ? r.body.maps : {}; }
export async function bikes(): Promise<{ connected: boolean; needsPermission?: boolean; bikes: { id: string; name: string; km: number; primary: boolean }[] }> {
  const r = await api("/api/strava/gear").catch(() => null);
  return r?.status === 200 ? r.body : { connected: false, bikes: [] };
}
/** Pull to refresh: read everything again, ask Strava for new rides, and re-read the diary weeks. */
export async function pullRefresh(): Promise<string> {
  const st = await refresh();
  if (st !== "live") return st === "offline" ? "No signal. Showing the last copy." : "Sign in to refresh.";
  await stravaSync();
  return "Up to date.";
}
export function withdrawAsk(at: string) {
  return change("log", (s) => { s.log = logOf(s); s.log.asks = s.log.asks!.filter((a) => a.at !== at || a.reply); });
}

/** Follow a race: a notice at 08:00 on each day of it you care about. */
export function toggleFollow(id: string) {
  return change("log", (s) => { s.log = logOf(s); const f = new Set(s.log.follow); f.has(id) ? f.delete(id) : f.add(id); s.log.follow = [...f]; });
}

/**
 * Add a route from a Komoot GPX, or re-measure one already in the library. The numbers go into
 * the routes document (so the planner, the walls and the coach see it at once); the map line and
 * height profile go to the server. An existing route keeps its photo and Komoot link.
 */
export async function importRoute(r: Route, detail: { track: [number, number][]; profile: [number, number][] }): Promise<string> {
  const had = current.routes.some((x) => x.id === r.id);
  const saved = await change("routes", (s) => {
    const i = s.routes.findIndex((x) => x.id === r.id);
    if (i >= 0) s.routes[i] = { ...s.routes[i], ...r, photo: s.routes[i].photo ?? r.photo, komoot: r.komoot ?? s.routes[i].komoot };
    else s.routes.push(r);
  });
  if (meta.sync !== "live") return "Saved on this phone only. Sign in and import it again to keep it.";
  const d = await api(`/api/routes/${encodeURIComponent(r.id)}`, { method: "POST", body: JSON.stringify(detail) }).catch(() => null);
  if (!saved) return "The library didn't save. Try again with a signal.";
  return d?.status === 200 ? (had ? `${r.n} re-measured.` : `${r.n} added to the library.`) : `${r.n} saved; its map didn't upload. Import it again later.`;
}
/** Bring a Komoot find into the library: unsurveyed (so weekday evenings treat it as unlit), weekend, with the ride to its start in the distance. */
export async function adoptExplore(e: import("./explore").Explore): Promise<string> {
  const approach = e.startKm != null && e.startKm > 1 ? Math.round(e.startKm * 20) / 10 : 0;
  const { litOnOsm } = await import("./explore");
  const lit = litOnOsm(e);
  const total = Math.round((e.km + approach) * 10) / 10;
  const dark = !lit && e.light?.dark.length ? [e.light.dark[0][0], e.light.dark[e.light.dark.length - 1][1]] : null;
  const r: Route = {
    id: e.id, n: e.name, km: total, gain: e.gain, steep: e.steep, u0: dark ? dark[0] + approach / 2 : null, u1: dark ? dark[1] + approach / 2 : null,
    weekend: true, weeknight: lit && total <= 45, lit, komoot: e.url, loop: e.loop, ...(e.startKm != null ? { startKm: e.startKm } : {}),
    ...(e.light ? { lightFrom: "osm" as const, osm: { lit: e.light.lit, likely: e.light.likely, unknown: e.light.unknown, unlit: e.light.unlit, data: "2026-10-25" } } : {}),
    note: `From Komoot.${approach ? ` Includes ${approach} km to the start and back.` : ""} ${lit ? `Lit on OpenStreetMap (${e.light!.lit}% tagged, ${e.light!.likely}% 30 mph streets, ${e.light!.unknown}% not known); your survey overrides it.` : dark ? `Unlit on OpenStreetMap from ${dark[0]} to ${dark[1]} km of the route; mark it yourself after a ride.` : "Lighting not surveyed yet."}`,
  };
  return importRoute(r, { track: e.track, profile: e.profile });
}
export async function routeDetail(id: string): Promise<{ track: [number, number][]; profile: [number, number][] } | null> {
  const r = await api(`/api/routes/${encodeURIComponent(id)}`).catch(() => null);
  return r?.status === 200 ? r.body : null;
}
